import { Link } from "react-router-dom";
import {
  ArrowRight, Stethoscope, FileSearch, Smartphone, CreditCard,
  ShieldCheck, CloudSun, Newspaper, Sparkles, Building2, Compass, Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProjectCard } from "@/components/ProjectCard";
import { projects, areas } from "@/data/projects";
import { useSeo } from "@/lib/seo";

const AREA_ICONS = {
  Stethoscope, FileSearch, Smartphone, CreditCard, ShieldCheck, CloudSun, Newspaper, Sparkles,
};

const featured = projects.slice(0, 3);

export default function Home() {
  useSeo({
    title: "Practical Technology for Real-World Problems",
    description:
      "HILDRETH TECHNOLOGIES LLC is a founder-led Florida software company building focused digital products across healthcare, medical policy, travel, privacy, media, and creator technology.",
    path: "/",
  });

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 ht-hero-glow" />
        <div className="absolute inset-0 ht-grid-lines opacity-60" />
        <div className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32 lg:py-40">
          <div className="max-w-3xl">
            <span
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-[#0B1527]/60 px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-slate-300"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-teal-400" />
              Founder-led Florida technology company
            </span>
            <h1
              data-testid="hero-heading"
              className="mt-6 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl"
            >
              Practical Technology for{" "}
              <span className="bg-gradient-to-r from-sky-400 to-teal-300 bg-clip-text text-transparent">
                Real-World Problems
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">
              HILDRETH TECHNOLOGIES LLC builds focused digital products across
              healthcare, medical policy, travel, privacy, media, and creator
              technology.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Button
                asChild
                size="lg"
                data-testid="hero-view-work"
                className="rounded-full bg-sky-500 px-7 text-base font-semibold text-[#040B16] hover:bg-sky-400"
              >
                <Link to="/our-work">
                  View Our Work <ArrowRight size={18} className="ml-2" />
                </Link>
              </Button>
              <a
                href="/contact"
                data-testid="hero-contact"
                className="inline-flex h-10 items-center justify-center rounded-full border border-slate-600 bg-transparent px-7 text-base font-semibold text-white transition-colors hover:bg-slate-800"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Company intro */}
      <section className="border-y border-slate-800/70 bg-[#0B1527]/40">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 md:grid-cols-3 md:py-20">
          <div className="md:col-span-2">
            <h2 className="font-display text-2xl font-semibold text-white sm:text-3xl">
              Software built around real workflows, not hype
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-400">
              HILDRETH TECHNOLOGIES LLC designs and develops focused digital
              products for healthcare professionals, travelers, privacy-conscious
              consumers, policy teams, media audiences, and online creators. Every
              product starts with a real problem and a clear, understandable
              solution.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {[
              { icon: Building2, label: "Florida LLC", sub: "Established July 8, 2026" },
              { icon: Compass, label: "Founder-led", sub: "Lisa Hildreth" },
              { icon: Layers, label: "Six live products", sub: "Across six industries" },
            ].map((f) => (
              <div key={f.label} className="flex items-center gap-4 rounded-lg border border-slate-800 bg-[#0B1527] p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-500/15 text-sky-300">
                  <f.icon size={20} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-white">{f.label}</p>
                  <p className="text-xs text-slate-400">{f.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas We Build In */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-sky-400">
            Areas We Build In
          </p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-white sm:text-4xl">
            Focused expertise across industries
          </h2>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {areas.map((a) => {
            const Icon = AREA_ICONS[a.icon] || Layers;
            return (
              <div
                key={a.title}
                data-testid={`area-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="ht-card rounded-xl p-6"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-gradient-to-br from-sky-500/20 to-teal-500/10 text-sky-300">
                  <Icon size={22} />
                </span>
                <h3 className="mt-4 text-base font-semibold leading-snug text-white">
                  {a.title}
                </h3>
              </div>
            );
          })}
        </div>
      </section>

      {/* Featured projects */}
      <section className="border-t border-slate-800/70 bg-[#0B1527]/40">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-wider text-sky-400">
                Featured Products
              </p>
              <h2 className="mt-3 font-display text-3xl font-semibold text-white sm:text-4xl">
                Real products, live today
              </h2>
            </div>
            <Button
              asChild
              variant="ghost"
              data-testid="home-view-all-work"
              className="rounded-full text-sky-300 hover:bg-slate-800 hover:text-white"
            >
              <Link to="/our-work">
                See all work <ArrowRight size={16} className="ml-1.5" />
              </Link>
            </Button>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {featured.map((p) => (
              <ProjectCard key={p.id} project={p} />
            ))}
          </div>
        </div>
      </section>

      {/* Founder statement */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-[#0B1527] to-[#040B16] p-8 sm:p-12">
          <p className="text-sm font-semibold uppercase tracking-wider text-teal-300">
            Founder-Led
          </p>
          <blockquote className="mt-4 max-w-3xl font-display text-2xl font-medium leading-snug text-white sm:text-3xl">
            “Practical technology built to solve real-world problems.”
          </blockquote>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
            HILDRETH TECHNOLOGIES LLC is led by founder and Managing Member Lisa
            Hildreth, who holds a master's degree in Technology Management and a
            bachelor's degree. Her experience in technology management, business
            operations, and digital product development informs the company's
            emphasis on useful, understandable technology.
          </p>
          <Button
            asChild
            variant="outline"
            data-testid="home-about-link"
            className="mt-8 rounded-full border-slate-600 bg-transparent text-white hover:bg-slate-800"
          >
            <Link to="/about">
              About the company <ArrowRight size={16} className="ml-2" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="border-t border-slate-800/70">
        <div className="mx-auto max-w-7xl px-5 py-20 text-center sm:px-8">
          <h2 className="mx-auto max-w-2xl font-display text-3xl font-semibold text-white sm:text-4xl">
            Interested in a product, partnership, or demonstration?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-400">
            Reach out for general inquiries, business opportunities, product
            questions, or media requests.
          </p>
          <Button
            asChild
            size="lg"
            data-testid="home-cta-contact"
            className="mt-8 rounded-full bg-sky-500 px-8 text-base font-semibold text-[#040B16] hover:bg-sky-400"
          >
            <Link to="/contact">
              Get in Touch <ArrowRight size={18} className="ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
